import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Film, Play, X, Loader2, Hourglass, Tag, Mic2 } from "lucide-react";

/**
 * Media · Videos tab (iter 31.2, 20 May 2026).
 *
 * Iter 30.8 made the storage path live — any MP4/MOV/WebM/MKV/AVI/M4V
 * dropped via the Shredder or Drive Watch now lands in `radio_videos` +
 * GridFS bucket `radio_video`, no LLM cost.
 *
 * Iter 31.8 (21 May 2026): BBFC-style ratings (U / PG / 12 / 15 / 18 /
 * unrated). Click the rating badge on any tile to cycle through the
 * ratings (PATCH /api/radio/videos/{id}/rating).
 */
const TOKEN_KEY = "msd_token";
const RATING_CYCLE = ["unrated", "U", "PG", "12", "15", "18"];

const RATING_STYLE = {
    unrated:  "border-ink/30 text-ink-soft",
    U:        "border-stamp-green text-stamp-green",
    PG:       "border-stamp-green text-stamp-green",
    "12":     "border-amber-600 text-amber-700",
    "15":     "border-orange-600 text-orange-700",
    "18":     "border-stamp-red text-stamp-red",
};

export default function RadioVideos() {
    const [videos, setVideos] = useState([]);
    const [busy, setBusy] = useState(true);
    const [err, setErr] = useState("");
    const [openVideo, setOpenVideo] = useState(null); // {id, title, url}
    const [promotingId, setPromotingId] = useState(null);
    const blobRef = useRef(null);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/radio/videos");
            setVideos(data.videos || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Could not load videos");
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Iter 31.86 — Promote a draft lyric video to karaoke grade via the
    // iter 31.68 Stage B route. Confirms with credit estimate, fires
    // POST /radio/videos/{id}/karaoke-grade, then polls the job until
    // done so the new karaoke variant shows up in this list.
    const promoteToKaraoke = useCallback(async (v, e) => {
        if (e) { e.stopPropagation(); e.preventDefault(); }
        const ok = window.confirm(
            `Promote "${v.title || v.filename || "Untitled"}" to karaoke grade?\n\n` +
            "· Whisper re-times every lyric line for exact karaoke timing\n" +
            "· Fresh cover image generated\n" +
            "· New MP4 stored alongside the draft (original is preserved)\n" +
            "· Cost: 1 credit (refunded if the render fails)",
        );
        if (!ok) return;
        setPromotingId(v.id);
        try {
            const { data: kicked } = await api.post(`/radio/videos/${v.id}/karaoke-grade`);
            const trackId = kicked?.job?.track_id;
            if (!trackId) throw new Error("kickoff missing track_id");
            window.dispatchEvent(new Event("shed:credits-changed"));
            // Poll status every 6s for up to 5 minutes. Karaoke renders
            // are slower than draft (Whisper transcribe + fresh cover +
            // ffmpeg) — typical 60-120s.
            const deadline = Date.now() + 5 * 60 * 1000;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                if (Date.now() > deadline) break;
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 6000));
                // eslint-disable-next-line no-await-in-loop
                const { data: status } = await api.get(`/radio/tracks/${trackId}/video/status`);
                const job = status?.job;
                if (job?.status === "done" || job?.status === "error") break;
            }
            await load();
        } catch (err2) {
            window.alert(err2?.response?.data?.detail || err2.message || "Karaoke promote failed");
        } finally {
            setPromotingId(null);
        }
    }, [load]);

    // Iter 31.103 — Promote a karaoke (or draft) video to MOTION grade
    // via Sora 2. Same poll pattern as karaoke promote but ~5x slower
    // (Sora gen is 3-5 minutes vs Whisper's ~30s).
    const promoteToMotion = useCallback(async (v, e) => {
        if (e) { e.stopPropagation(); e.preventDefault(); }
        const ok = window.confirm(
            `Promote "${v.title || v.filename || "Untitled"}" to MOTION grade?\n\n` +
            "· Sora 2 generates a 4-second motion clip\n" +
            "· Looped under audio with subtitles burned on top\n" +
            "· New MP4 stored alongside the karaoke/draft (original is preserved)\n" +
            "· Cost: 3 credits (refunded if the render fails)\n" +
            "· Takes 3-5 minutes — Sora is slow but worth it",
        );
        if (!ok) return;
        setPromotingId(v.id);
        try {
            const { data: kicked } = await api.post(`/radio/videos/${v.id}/sora-motion`);
            const trackId = kicked?.job?.track_id;
            if (!trackId) throw new Error("kickoff missing track_id");
            window.dispatchEvent(new Event("shed:credits-changed"));
            // Poll for up to 12 minutes — Sora is the long pole.
            const deadline = Date.now() + 12 * 60 * 1000;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                if (Date.now() > deadline) break;
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 10000));
                // eslint-disable-next-line no-await-in-loop
                const { data: status } = await api.get(`/radio/tracks/${trackId}/video/status`);
                const job = status?.job;
                if (job?.status === "done" || job?.status === "error") break;
            }
            await load();
        } catch (err2) {
            window.alert(err2?.response?.data?.detail || err2.message || "Sora motion promote failed");
        } finally {
            setPromotingId(null);
        }
    }, [load]);

    // Click a tile → fetch the stream as an authenticated blob (same pattern
    // the radio nub uses for audio — keeps the bearer-JWT auth working).
    const playVideo = useCallback(async (v) => {
        try {
            if (blobRef.current) {
                URL.revokeObjectURL(blobRef.current);
                blobRef.current = null;
            }
            setOpenVideo({ id: v.id, title: v.title || v.filename, url: null, loading: true });
            // Tell the audio nub to mute itself for the duration. The nub
            // reads `shed_radio_muted` on its own ticks, so this is the
            // cheapest cross-component handoff.
            try { localStorage.setItem("shed_radio_muted", "1"); } catch (_) {}
            const token = localStorage.getItem(TOKEN_KEY) || "";
            const resp = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/radio/videos/${v.id}/stream`,
                { headers: token ? { Authorization: `Bearer ${token}` } : {} },
            );
            if (!resp.ok) throw new Error(`stream ${resp.status}`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            blobRef.current = url;
            setOpenVideo({ id: v.id, title: v.title || v.filename, url, loading: false });
        } catch (e) {
            setOpenVideo(null);
            setErr(`Could not stream: ${e.message || e}`);
        }
    }, []);

    const closeVideo = useCallback(() => {
        if (blobRef.current) {
            URL.revokeObjectURL(blobRef.current);
            blobRef.current = null;
        }
        setOpenVideo(null);
        // Hand the radio nub back its previous state — the player will read
        // its own preference on next interaction, which leaves the operator
        // with the same mute state they had before opening the video.
    }, []);

    // Cycle rating: unrated → U → PG → 12 → 15 → 18 → unrated. Optimistic UI;
    // reverts on API failure. Stops the tile click so we don't open the player.
    const cycleRating = useCallback(async (e, v) => {
        e.stopPropagation();
        const current = v.rating || "unrated";
        const i = RATING_CYCLE.indexOf(current);
        const next = RATING_CYCLE[(i + 1) % RATING_CYCLE.length];
        setVideos((prev) => prev.map((x) => (x.id === v.id ? { ...x, rating: next } : x)));
        try {
            await api.patch(`/radio/videos/${v.id}/rating`, { rating: next });
        } catch (err) {
            // Revert on failure
            setVideos((prev) => prev.map((x) => (x.id === v.id ? { ...x, rating: current } : x)));
            setErr(`Could not set rating: ${err?.response?.data?.detail || err.message}`);
        }
    }, []);

    return (
        <div className="space-y-6" data-testid="radio-videos-tab">
            {err && (
                <div className="paper-card border-2 border-stamp-red p-4" data-testid="radio-videos-error">
                    <div className="font-heading text-sm text-stamp-red tracking-[0.2em] mb-1">FAILURE</div>
                    <div className="font-body text-xs text-ink">{err}</div>
                </div>
            )}

            {busy ? (
                <div className="paper-card p-12 text-center" data-testid="radio-videos-loading">
                    <Loader2 className="w-8 h-8 text-ink-soft mx-auto mb-3 animate-spin" />
                    <div className="label-typed text-[10px]">Reading the vault…</div>
                </div>
            ) : videos.length === 0 ? (
                <div className="paper-card border border-dashed border-ink/25 p-12 text-center" data-testid="radio-videos-empty">
                    <Film className="w-10 h-10 text-ink mx-auto mb-3 opacity-40" strokeWidth={1} />
                    <div className="font-heading uppercase tracking-[0.22em] text-lg text-ink-faint">
                        No videos on file
                    </div>
                    <div className="label-typed mt-3 text-[10px] leading-relaxed">
                        Drop an MP4 into the Shredder &mdash; or your Drive Watch <code className="px-1 bg-paper-light border border-ink/20 text-[11px]">_Inbox</code> &mdash;
                        and it will land here. No LLM cost, max 250&nbsp;MB.
                    </div>
                </div>
            ) : (
                <>
                    <div className="label-typed text-[10px]">
                        {videos.length} video{videos.length === 1 ? "" : "s"} on file &middot; newest first
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="radio-videos-grid">
                        {videos.map((v) => {
                            const rating = v.rating || "unrated";
                            const ratingCls = RATING_STYLE[rating] || RATING_STYLE.unrated;
                            return (
                            <div
                                key={v.id}
                                className="paper-card p-4 hover:border-stamp-red border border-ink/20 transition-colors group relative"
                                data-testid={`radio-video-${v.id}`}
                            >
                                {/* Iter 31.86 — Promote-to-karaoke chip
                                    (only on draft lyric videos). */}
                                {v.grade === "draft" && v.source === "lyric_video_v1" && (
                                    <button
                                        onClick={(e) => promoteToKaraoke(v, e)}
                                        disabled={promotingId === v.id}
                                        data-testid={`radio-video-karaoke-${v.id}`}
                                        title="Re-time with Whisper for exact karaoke timing · 1 credit"
                                        className="absolute top-2 left-2 inline-flex items-center gap-1 border border-stamp-red text-stamp-red bg-paper-light/80 hover:bg-stamp-red hover:text-paper px-2 py-0.5 text-[10px] font-heading uppercase tracking-[0.18em] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {promotingId === v.id ? (
                                            <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Promoting…</>
                                        ) : (
                                            <><Mic2 className="w-2.5 h-2.5" strokeWidth={1.8} /> Karaoke</>
                                        )}
                                    </button>
                                )}
                                {v.grade === "karaoke" && (
                                    <div className="absolute top-2 left-2 flex items-center gap-1">
                                        <span
                                            className="inline-flex items-center gap-1 border border-stamp-green text-stamp-green bg-paper-light/80 px-2 py-0.5 text-[10px] font-heading uppercase tracking-[0.18em]"
                                            data-testid={`radio-video-karaoke-badge-${v.id}`}
                                            title="Karaoke grade · Whisper-timed"
                                        >
                                            <Mic2 className="w-2.5 h-2.5" strokeWidth={1.8} /> Karaoke
                                        </span>
                                        <button
                                            onClick={(e) => promoteToMotion(v, e)}
                                            disabled={promotingId === v.id}
                                            data-testid={`radio-video-motion-${v.id}`}
                                            title="Promote to MOTION grade with Sora 2 · 3 credits · 3-5 min"
                                            className="inline-flex items-center gap-1 border border-stamp-red text-stamp-red bg-paper-light/80 hover:bg-stamp-red hover:text-paper px-2 py-0.5 text-[10px] font-heading uppercase tracking-[0.18em] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {promotingId === v.id ? (
                                                <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Motion…</>
                                            ) : (
                                                <><Film className="w-2.5 h-2.5" strokeWidth={1.8} /> + Motion</>
                                            )}
                                        </button>
                                    </div>
                                )}
                                {v.grade === "motion" && (
                                    <span
                                        className="absolute top-2 left-2 inline-flex items-center gap-1 border border-stamp-red text-stamp-red bg-paper-light/80 px-2 py-0.5 text-[10px] font-heading uppercase tracking-[0.18em]"
                                        data-testid={`radio-video-motion-badge-${v.id}`}
                                        title="Motion grade · Sora 2 video backdrop"
                                    >
                                        <Film className="w-2.5 h-2.5" strokeWidth={1.8} /> Motion
                                    </span>
                                )}
                                <button
                                    onClick={(e) => cycleRating(e, v)}
                                    className={`absolute top-2 right-2 inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-heading uppercase tracking-[0.18em] bg-paper-light/80 hover:opacity-80 transition-opacity ${ratingCls}`}
                                    data-testid={`radio-video-rating-${v.id}`}
                                    title={`Rating: ${rating} · click to cycle`}
                                    aria-label={`Rating ${rating}, click to change`}
                                >
                                    <Tag className="w-2.5 h-2.5" strokeWidth={1.8} />
                                    {rating === "unrated" ? "—" : rating}
                                </button>
                                <button
                                    onClick={() => playVideo(v)}
                                    className="w-full text-left"
                                    data-testid={`radio-video-play-${v.id}`}
                                    aria-label={`Play ${v.title || v.filename}`}
                                >
                                    <div className="flex items-start gap-3 pr-14">
                                        <div className="w-12 h-12 border-2 border-ink/35 group-hover:border-stamp-red flex items-center justify-center shrink-0 transition-colors">
                                            <Play className="w-5 h-5 text-ink group-hover:text-stamp-red" strokeWidth={1.5} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-heading uppercase tracking-[0.06em] text-sm text-ink truncate">
                                                {v.title || v.filename || "Untitled"}
                                            </div>
                                            <div className="font-body text-[11px] text-ink-soft mt-1">
                                                {fmtSize(v.size_bytes)} &middot; {fmtTime(v.uploaded_at)}
                                            </div>
                                            <div className="font-body text-[10px] text-ink-faint mt-1 truncate">
                                                {v.content_type || "video/mp4"}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Player modal */}
            {openVideo && (
                <div
                    className="fixed inset-0 bg-ink/80 z-[2147483641] flex items-center justify-center p-4"
                    onClick={closeVideo}
                    data-testid="radio-video-modal"
                >
                    <div
                        className="paper-card border-2 border-ink/40 max-w-4xl w-full relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between border-b border-ink/15 px-4 py-3">
                            <div className="font-heading uppercase tracking-[0.06em] text-sm text-ink truncate pr-3">
                                {openVideo.title}
                            </div>
                            <button
                                onClick={closeVideo}
                                className="text-ink-soft hover:text-stamp-red"
                                data-testid="radio-video-modal-close"
                                aria-label="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="bg-black aspect-video flex items-center justify-center">
                            {openVideo.loading ? (
                                <div className="flex items-center gap-2 text-paper-light">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="font-body text-xs uppercase tracking-[0.2em]">Loading…</span>
                                </div>
                            ) : (
                                <video
                                    src={openVideo.url}
                                    controls
                                    autoPlay
                                    playsInline
                                    className="max-h-[70vh] max-w-full"
                                    data-testid="radio-video-player"
                                >
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Note about Mureka generation — preserved per earlier roadmap */}
            <div className="paper-card border border-ink/15 p-4" data-testid="radio-videos-mureka-note">
                <div className="flex items-start gap-3">
                    <Hourglass className="w-4 h-4 text-ink-soft mt-0.5 shrink-0" />
                    <div className="font-body text-[11px] text-ink-soft leading-relaxed">
                        <strong className="text-ink">Generation pending upstream</strong>
                        &mdash; the day Mureka publish an official video endpoint, a
                        &ldquo;Generate from a song&rdquo; panel will appear above this
                        library. Until then the Shed plays whatever video you file. No
                        third-party wrappers.
                    </div>
                </div>
            </div>
        </div>
    );
}

function fmtSize(bytes) {
    if (!bytes || bytes < 1024) return `${bytes || 0} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch (_) {
        return iso;
    }
}
